import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Empty, Input, Pagination, Select, Skeleton } from 'antd';
import { DashboardOutlined, MoreOutlined, SearchOutlined } from '@ant-design/icons';
import { FaShip } from 'react-icons/fa';
import { formatRefDate } from '../../utils/formatRefDate';

const PAGE_SIZE = 10;

export default function VesselList({ vessels, pendingCodes, loading, search, status, selectedKey, onSearch, onStatus, onSelect }) {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const placeholders = pendingCodes.length ? pendingCodes : loading && !vessels.length ? ['1', '2', '3', '4', '5'] : [];
    const visibleVessels = useMemo(() => vessels.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [page, vessels]);

    useEffect(() => { setPage(1); }, [search, status, vessels.length]);

    const openDashboard = vessel => {
        localStorage.setItem('Filter', JSON.stringify({
            KodeCabang: vessel.KodeCabang,
            NamaCabang: vessel.NamaCabang,
            ShipCode: vessel.ShipCode,
            ShipName: vessel.ShipName,
        }));
        navigate(`/dashboard?KodeCabang=${vessel.KodeCabang}&ShipCode=${vessel.ShipCode}`);
    };

    const actionMenu = vessel => ({
        items: [
            { key: 'select', label: 'Show vessel' },
            { key: 'dashboard', label: 'Open Dashboard', icon: <DashboardOutlined /> },
        ],
        onClick: ({ key }) => key === 'dashboard' ? openDashboard(vessel) : onSelect(vessel.key),
    });

    return <section className="monitoring-vessel-list" aria-labelledby="monitoring-vessel-list-title">
        <header className="monitoring-panel-heading">
            <div className="monitoring-panel-title">
                <span className="monitoring-section-icon" aria-hidden="true"><FaShip /></span>
                <div><h2 id="monitoring-vessel-list-title">Vessel List</h2><p>{vessels.length} vessels in the current view</p></div>
            </div>
            <span className="monitoring-count">{vessels.length}</span>
        </header>

        <div className="monitoring-list-controls">
            <Input aria-label="Search vessels" allowClear prefix={<SearchOutlined />} placeholder="Search vessel or account..." value={search}
                onChange={event => onSearch(event.target.value)} />
            <Select aria-label="Filter vessel status" value={status} onChange={onStatus} options={[
                { value: 'all', label: 'All Status' }, { value: 'online', label: 'Online' }, { value: 'offline', label: 'Offline' },
            ]} />
        </div>

        <div className="monitoring-list-table" role="table" aria-label="Loaded vessels">
            <div className="monitoring-list-head" role="row">
                <span role="columnheader">Vessel</span><span role="columnheader">Status</span><span role="columnheader">Last Reading</span><span role="columnheader">Actions</span>
            </div>
            <div className="monitoring-list-body">
                {visibleVessels.map(vessel => <div className={`monitoring-vessel-row ${selectedKey === vessel.key ? 'is-selected' : ''}`} role="row" key={vessel.key}>
                    <button type="button" className="monitoring-vessel-row__select" aria-pressed={selectedKey === vessel.key}
                        onClick={() => onSelect(vessel.key)} onDoubleClick={() => openDashboard(vessel)} title="Double-click to open Dashboard">
                        <span className="monitoring-vessel-name" role="cell"><i aria-hidden="true"><FaShip /></i><strong>{vessel.ShipName || vessel.ShipCode || 'Unnamed vessel'}</strong></span>
                        <span className={`monitoring-status ${vessel.status ? 'is-online' : 'is-offline'}`} role="cell"><i />{vessel.status ? 'Online' : 'Offline'}</span>
                        <time role="cell">{formatRefDate(vessel.lastReading)}</time>
                    </button>
                    <Dropdown trigger={['click']} menu={actionMenu(vessel)}>
                        <Button type="text" aria-label={`Actions for ${vessel.ShipName || vessel.ShipCode}`} icon={<MoreOutlined />} />
                    </Dropdown>
                </div>)}

                {placeholders.map(code => <div className="monitoring-vessel-row is-loading" role="status" key={`pending-${code}`} aria-label={`Loading vessel ${code}`}>
                    <div className="monitoring-vessel-row__skeleton"><Skeleton.Avatar active size="small" /><Skeleton.Input active size="small" /><Skeleton.Input active size="small" /></div>
                </div>)}

                {!loading && !visibleVessels.length && !placeholders.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No vessels match the current filter" />}
            </div>
        </div>

        <Pagination size="small" current={page} pageSize={PAGE_SIZE} total={vessels.length} showSizeChanger={false} hideOnSinglePage
            onChange={setPage} showTotal={total => `${total} vessels`} />
    </section>;
}

VesselList.propTypes = {
    vessels: PropTypes.array.isRequired,
    pendingCodes: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    search: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    selectedKey: PropTypes.string,
    onSearch: PropTypes.func.isRequired,
    onStatus: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};
